import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getFestivals,
  deleteFestival
} from '../services/festivals.service';

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, festivalName, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-red-400">warning</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Delete Festival</h3>
            <p className="text-white/50 mb-1">
              Are you sure you want to delete <strong className="text-white">"{festivalName}"</strong>?
            </p>
            <p className="text-sm text-white/30 mt-2">This action cannot be undone.</p>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Modal
const PreviewModal = ({ isOpen, onClose, festival }) => {
  if (!isOpen || !festival) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-2xl animate-fade-in overflow-hidden">
          {/* Image */}
          <div className="relative h-64 overflow-hidden">
            <img
              src={festival.image_url}
              alt={festival.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-2xl font-semibold text-white">{festival.name}</h3>
              <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium">
                {festival.month}
              </span>
            </div>
            
            <p className="text-white/60 leading-relaxed">{festival.description}</p>
            
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div>
                <p className="text-sm text-white/40 mb-1">Main States</p>
                <p className="text-white">{festival.main_states}</p>
              </div>
              <div>
                <p className="text-sm text-white/40 mb-1">Best Places</p>
                <p className="text-white">{festival.best_places}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const FESTIVALS_PER_PAGE = 9;

function Festivals() {
  const navigate = useNavigate();
  const [festivals, setFestivals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Modal states
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, festival: null });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, festival: null });

  const fetchFestivals = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getFestivals();
      setFestivals(response.data || response || []);
    } catch (error) {
      console.error('Error fetching festivals:', error);
      toast.error('Failed to load festivals');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFestivals();
  }, [fetchFestivals]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonth]);

  const openDeleteModal = (festival) => {
    setDeleteModal({ isOpen: true, festival });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, festival: null });
  };

  const handleDelete = async () => {
    if (!deleteModal.festival) return;
    
    try {
      setIsDeleting(true);
      await deleteFestival(deleteModal.festival.id);
      toast.success('Festival deleted successfully');
      closeDeleteModal();
      fetchFestivals();
    } catch (error) {
      console.error('Error deleting festival:', error);
      toast.error(error.response?.data?.message || 'Failed to delete festival');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredFestivals = festivals.filter(festival => {
    const matchesSearch = !searchTerm || 
      festival.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      festival.main_states.toLowerCase().includes(searchTerm.toLowerCase()) ||
      festival.best_places.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMonth = !selectedMonth || festival.month === selectedMonth;
    
    return matchesSearch && matchesMonth;
  });

  const totalPages = Math.max(1, Math.ceil(filteredFestivals.length / FESTIVALS_PER_PAGE));
  const startIndex = (currentPage - 1) * FESTIVALS_PER_PAGE;
  const paginatedFestivals = filteredFestivals.slice(startIndex, startIndex + FESTIVALS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Group festivals by month
  const festivalsByMonth = filteredFestivals.reduce((acc, festival) => {
    const month = festival.month;
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(festival);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extralight tracking-tight text-white">
            Festivals<span className="text-purple-400 font-bold">.</span>
          </h1>
          <p className="text-sm text-white/40 mt-1">Manage Indian festivals and celebrations</p>
        </div>
        <button
          onClick={() => navigate('/festivals/new')}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-purple-500/25"
        >
          <span className="material-symbols-outlined text-lg">add</span>
          Add Festival
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-purple-400">celebration</span>
            </div>
            <div>
              <p className="text-3xl font-light text-white">{festivals.length}</p>
              <p className="text-sm text-white/40">Total Festivals</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-indigo-400">calendar_month</span>
            </div>
            <div>
              <p className="text-3xl font-light text-white">
                {Object.keys(festivalsByMonth).length}
              </p>
              <p className="text-sm text-white/40">Months Covered</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-cyan-400">search</span>
            </div>
            <div>
              <p className="text-3xl font-light text-white">{filteredFestivals.length}</p>
              <p className="text-sm text-white/40">Showing Results</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-white/30">search</span>
            <input
              type="text"
              placeholder="Search festivals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
            />
          </div>
          
          {/* Month Filter */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50 transition-all"
          >
            <option value="" className="bg-[#0a0a0f]">All Months</option>
            {MONTHS.map(month => (
              <option key={month} value={month} className="bg-[#0a0a0f]">{month}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Festivals Grid */}
      {filteredFestivals.length > 0 ? (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedFestivals.map((festival) => (
            <div
              key={festival.id}
              className="group bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={festival.image_url}
                  alt={festival.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
                
                {/* Month Badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                  <span className="text-sm font-medium text-white">{festival.month}</span>
                </div>
                
                {/* Actions */}
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setPreviewModal({ isOpen: true, festival })}
                    className="p-2 bg-black/50 backdrop-blur-sm text-white rounded-lg hover:bg-black/70 transition-colors"
                    title="Preview"
                  >
                    <span className="material-symbols-outlined text-lg">visibility</span>
                  </button>
                  <button
                    onClick={() => navigate(`/festivals/edit/${festival.id}`)}
                    className="p-2 bg-black/50 backdrop-blur-sm text-white rounded-lg hover:bg-purple-500 transition-colors"
                    title="Edit"
                  >
                    <span className="material-symbols-outlined text-lg">edit</span>
                  </button>
                  <button
                    onClick={() => openDeleteModal(festival)}
                    className="p-2 bg-black/50 backdrop-blur-sm text-white rounded-lg hover:bg-red-500 transition-colors"
                    title="Delete"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5 space-y-3">
                <h3 className="text-lg font-semibold text-white line-clamp-1">{festival.name}</h3>
                <p className="text-sm text-white/50 line-clamp-2">{festival.description}</p>
                
                <div className="pt-3 border-t border-white/5 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="material-symbols-outlined text-base text-purple-400">location_on</span>
                    <span className="text-white/60 line-clamp-1">{festival.main_states}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="material-symbols-outlined text-base text-indigo-400">place</span>
                    <span className="text-white/60 line-clamp-1">{festival.best_places}</span>
                  </div>
                </div>
                
                <p className="text-xs text-white/30 pt-2">
                  Added {formatDate(festival.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 px-4 py-2.5 bg-white/[0.02] border border-white/10 rounded-xl text-white font-medium hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-lg">chevron_left</span>
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`min-w-[2.5rem] py-2.5 rounded-xl font-medium transition-all ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25'
                      : 'bg-white/[0.02] border border-white/10 text-white hover:bg-white/5'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 px-4 py-2.5 bg-white/[0.02] border border-white/10 rounded-xl text-white font-medium hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <span className="material-symbols-outlined text-lg">chevron_right</span>
            </button>
          </div>
        )}
        </>
      ) : (
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
            <span className="material-symbols-outlined text-3xl text-white/20">celebration</span>
          </div>
          <p className="font-medium text-white mb-1">
            {searchTerm || selectedMonth ? 'No festivals found' : 'No festivals yet'}
          </p>
          <p className="text-white/40 text-sm">
            {searchTerm || selectedMonth ? 'Try adjusting your filters' : 'Add your first festival to get started'}
          </p>
          {!searchTerm && !selectedMonth && (
            <button
              onClick={() => navigate('/festivals/new')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-purple-400 bg-purple-500/10 rounded-lg font-medium hover:bg-purple-500/20 transition-colors"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              Add Festival
            </button>
          )}
        </div>
      )}

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        festivalName={deleteModal.festival?.name}
        isLoading={isDeleting}
      />

      {/* Preview Modal */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, festival: null })}
        festival={previewModal.festival}
      />
    </div>
  );
}

export default Festivals;
